import type { CarrierNumber } from "@voice/api-contract";
export type PortalNumber = {
  id: string;
  e164: string;
  status: string;
  carrier: string;
  account_id: string | null;
  account_name: string | null;
  portal_linked_at?: string | null;
};
export type OwnedNumber = {
  number: string;
  carrier: string;
  carrierStatus: string | null;
  portalStatus: string | null;
  accountId: string | null;
  accountName: string | null;
  connection: string | null;
  assignmentChecked: boolean;
  twilioObservations?: {
    carrier: string;
    status: string;
    connection: string | null;
  }[];
};
export function mergeOwnedNumbers(
  local: PortalNumber[],
  telnyx: CarrierNumber[],
  flowroute: { label: string; items: CarrierNumber[] }[] = [],
  assignmentsComplete = true,
  twilio: { label: string; items: CarrierNumber[] }[] = [],
): OwnedNumber[] {
  const rows = new Map<string, OwnedNumber>();
  for (const item of local)
    rows.set(item.e164, {
      number: item.e164,
      carrier: item.carrier,
      carrierStatus: null,
      portalStatus: item.portal_linked_at
        ? "linked; calling setup not verified"
        : item.status,
      accountId: item.account_id,
      accountName: item.account_name,
      connection: null,
      assignmentChecked: true,
    });
  for (const item of telnyx) {
    const existing = rows.get(item.number);
    rows.set(item.number, {
      number: item.number,
      carrier: "Telnyx",
      carrierStatus: item.status,
      portalStatus: existing?.portalStatus ?? null,
      accountId: existing?.accountId ?? null,
      accountName: existing?.accountName ?? null,
      connection: item.connectionName || item.connectionId,
      assignmentChecked: existing?.assignmentChecked ?? assignmentsComplete,
    });
  }
  for (const account of flowroute)
    for (const item of account.items) {
      const existing = rows.get(item.number);
      const carrier = `Flowroute ${account.label}`;
      const overlap =
        existing?.carrierStatus !== undefined &&
        existing.carrierStatus !== null;
      rows.set(item.number, {
        number: item.number,
        carrier: overlap ? `${existing!.carrier} / ${carrier}` : carrier,
        carrierStatus: overlap
          ? `${existing!.carrier}: ${existing!.carrierStatus}; ${carrier}: ${item.status}`
          : item.status,
        portalStatus: existing?.portalStatus ?? null,
        accountId: existing?.accountId ?? null,
        accountName: existing?.accountName ?? null,
        connection: existing?.connection ?? null,
        assignmentChecked: existing?.assignmentChecked ?? assignmentsComplete,
      });
    }
  for (const account of twilio)
    for (const item of account.items) {
      const existing = rows.get(item.number);
      const carrier = `Twilio ${account.label}`;
      const connection = item.connectionName || item.connectionId;
      if (existing) {
        const savedCarrierMatches =
          existing.carrier.toLowerCase() === carrier.toLowerCase() ||
          existing.carrier.toLowerCase() === "twilio";
        rows.set(item.number, {
          ...existing,
          carrierStatus: savedCarrierMatches
            ? item.status
            : existing.carrierStatus,
          connection: savedCarrierMatches ? connection : existing.connection,
          twilioObservations: savedCarrierMatches
            ? (existing.twilioObservations ?? [])
            : [
                ...(existing.twilioObservations ?? []),
                { carrier, status: item.status, connection },
              ],
        });
      } else {
        rows.set(item.number, {
          number: item.number,
          carrier,
          carrierStatus: item.status,
          portalStatus: null,
          accountId: null,
          accountName: null,
          connection,
          assignmentChecked: assignmentsComplete,
        });
      }
    }
  return [...rows.values()].sort((left, right) =>
    left.number.localeCompare(right.number),
  );
}
